import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/config";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PhoneInput } from "@/components/PhoneInput";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Activity, Eye, EyeOff } from "lucide-react";
import PropTypes from 'prop-types';
import { ReticleSignal, signal } from '@/reticle';

export default function Login({ onLogin }) {
  const navigate = useNavigate();
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    password: "",
    confirmPassword: "",
    qualification: "",
    city: ""
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Password validation for signup
  const passwordsMatch = formData.password === formData.confirmPassword;
  const canSubmitSignup = formData.password && formData.confirmPassword && passwordsMatch;

  // Forgot password state
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [sendingReset, setSendingReset] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isLogin) {
        const response = await axios.post(`${API}/auth/login`, {
          email: formData.email,
          password: formData.password
        });
        onLogin(response.data.token, response.data.user_id);
        signal(ReticleSignal.AUTH_LOGGED_IN, {
          userId: response.data.user_id,
        });
        toast.success("Welcome back!");
        navigate("/");
      } else {
        const response = await axios.post(`${API}/auth/register`, formData);
        onLogin(response.data.token, response.data.doctor_id);
        signal(ReticleSignal.AUTH_REGISTERED, {
          userId: response.data.doctor_id,
        });
        toast.success("Account created successfully!");
        navigate("/");
      }
    } catch (error) {
      console.error("Login error:", error);
      if (!error.response) {
        toast.error("Network error: Cannot reach server. Please check your connection.");
      } else {
        toast.error(error.response?.data?.detail || "Authentication failed");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotEmail) {
      toast.error("Please enter your email address");
      return;
    }

    setSendingReset(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: forgotEmail });
      toast.success("If an account exists with this email, a reset link has been sent.");
      setShowForgotPassword(false);
      setForgotEmail("");
    } catch {
      toast.error("Failed to send reset email. Please try again.");
    } finally {
      setSendingReset(false);
    }
  };

  return (
    <div className="login-container" data-testid="login-container">
      <div className="login-card">
        <div className="login-header">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
            <Activity size={32} color="#0ea5e9" />
            <h1>ClinicNode</h1>
          </div>
          <p>Manage your practice with ease</p>
        </div>

        <div className="form-tabs">
          <button
            data-testid="login-tab"
            className={`form-tab ${isLogin ? 'active' : ''}`}
            onClick={() => {
              setIsLogin(true);
              setShowPassword(false);
              setShowConfirmPassword(false);
            }}
          >
            Login
          </button>
          <button
            data-testid="signup-tab"
            className={`form-tab ${!isLogin ? 'active' : ''}`}
            onClick={() => {
              setIsLogin(false);
              setShowPassword(false);
              setShowConfirmPassword(false);
            }}
          >
            Sign Up
          </button>
        </div>

        <form onSubmit={handleSubmit} data-testid="auth-form">
          {!isLogin && (
            <>
              <div style={{ marginBottom: '1rem' }}>
                <Label htmlFor="name">Full Name</Label>
                <Input
                  id="name"
                  data-testid="name-input"
                  placeholder="Enter your name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required={!isLogin}
                />
              </div>
              <div style={{ marginBottom: '1rem' }}>
                <Label htmlFor="phone">Phone</Label>
                <PhoneInput
                  id="phone"
                  data-testid="phone-input"
                  placeholder="Enter phone number"
                  value={formData.phone}
                  onChange={(val) => setFormData({ ...formData, phone: val })}
                  required={!isLogin}
                />
              </div>
            </>
          )}

          <div style={{ marginBottom: '1rem' }}>
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              data-testid="email-input"
              type="email"
              placeholder="Enter email address"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
            />
          </div>

          <div style={{ marginBottom: '1rem' }}>
            <Label htmlFor="password">Password</Label>
            <div style={{ position: 'relative' }}>
              <Input
                id="password"
                data-testid="password-input"
                type={showPassword ? "text" : "password"}
                placeholder="••••••••"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
                style={{ paddingRight: '2.5rem' }}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={{
                  position: 'absolute',
                  right: '0.75rem',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'transparent',
                  border: 'none',
                  color: '#64748b',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  padding: 0
                }}
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>

          {!isLogin && (
            <div style={{ marginBottom: '1rem' }}>
              <Label htmlFor="confirmPassword">Confirm Password</Label>
              <div style={{ position: 'relative' }}>
                <Input
                  id="confirmPassword"
                  data-testid="confirm-password-input"
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={formData.confirmPassword}
                  onChange={(e) => {
                    const confirmPassword = e.target.value;
                    setFormData({ ...formData, confirmPassword });
                    if (confirmPassword && formData.password !== confirmPassword) {
                      signal('auth:password-mismatch');
                    }
                  }}
                  required
                  style={{ paddingRight: '2.5rem' }}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  style={{
                    position: 'absolute',
                    right: '0.75rem',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'transparent',
                    border: 'none',
                    color: '#64748b',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    padding: 0
                  }}
                >
                  {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              {formData.confirmPassword && !passwordsMatch && (
                <p data-testid="password-mismatch-error" style={{ color: '#ef4444', fontSize: '0.8rem', marginTop: '0.25rem' }}>
                  Passwords do not match
                </p>
              )}
            </div>
          )}

          {!isLogin && (
            <>
              <div style={{ marginBottom: '1rem' }}>
                <Label htmlFor="qualification">Qualification</Label>
                <Input
                  id="qualification"
                  data-testid="qualification-input"
                  placeholder="Enter qualification"
                  value={formData.qualification}
                  onChange={(e) => setFormData({ ...formData, qualification: e.target.value })}
                  required={!isLogin}
                />
              </div>
              <div style={{ marginBottom: '1rem' }}>
                <Label htmlFor="city">City</Label>
                <Input
                  id="city"
                  data-testid="city-input"
                  placeholder="Enter City"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  required={!isLogin}
                />
              </div>
            </>
          )}

          <Button
            data-testid="submit-button"
            type="submit"
            className="w-full"
            disabled={loading || (!isLogin && !canSubmitSignup)}
            style={{
              background: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)',
              marginTop: '1.5rem'
            }}
          >
            {loading ? "Processing..." : isLogin ? "Login" : "Sign Up"}
          </Button>

          {isLogin && (
            <div style={{ textAlign: 'center', marginTop: '1rem' }}>
              <button
                type="button"
                onClick={() => setShowForgotPassword(true)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#0ea5e9',
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                  textDecoration: 'underline'
                }}
              >
                Forgot your password?
              </button>
            </div>
          )}
        </form>
      </div>

      {/* Forgot Password Dialog */}
      <Dialog open={showForgotPassword} onOpenChange={setShowForgotPassword}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Reset Password</DialogTitle>
            <DialogDescription>
              Enter your email address and we&apos;ll send you a link to reset your password.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleForgotPassword}>
            <div style={{ marginBottom: '1rem' }}>
              <Label htmlFor="forgot-email">Email Address</Label>
              <Input
                id="forgot-email"
                type="email"
                placeholder="doctor@example.com"
                value={forgotEmail}
                onChange={(e) => setForgotEmail(e.target.value)}
                required
              />
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <Button type="button" variant="outline" onClick={() => setShowForgotPassword(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={sendingReset}>
                {sendingReset ? "Sending..." : "Send Reset Link"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

Login.propTypes = {
  onLogin: PropTypes.func.isRequired
};
